import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWsEvent, useWebSocket } from "@/context/WebSocketContext";
import Tx from "@/components/Tx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const { user } = useAuth();
  const { status: wsStatus } = useWebSocket();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);

  const signedIn = user && user.email;

  useEffect(() => {
    if (!signedIn) return;
    const load = () => axios.get(`${API}/notifications`).then((r) => setItems(r.data || [])).catch(() => {});
    load();
    // Reduced polling (60s) since WebSocket handles real-time push
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [signedIn]);

  // Real-time: prepend new notifications coming via WS
  useWsEvent("notification", (notif) => {
    if (!notif || !notif.id) return;
    setItems((prev) => {
      if (prev.some((p) => p.id === notif.id)) return prev;
      return [notif, ...prev].slice(0, 200);
    });
    // Optional sound
    try {
      const a = new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=");
      a.volume = 0.4;
      a.play().catch(() => {});
    } catch {}
  }, [signedIn]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  if (!signedIn) return null;

  const unread = items.filter((i) => !i.read);

  const markAllRead = async () => {
    if (!unread.length) return;
    await axios.post(`${API}/notifications/read`, { ids: unread.map((i) => i.id) }).catch(() => {});
    setItems((arr) => arr.map((i) => ({ ...i, read: true })));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="nav-notif-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        className="relative w-10 h-10 grid place-items-center rounded-full bg-white/[0.06] hover:bg-white/[0.12] text-white/85 hover:text-[#00AFFF] transition-all"
      >
        <Bell size={18} />
        {wsStatus === "connected" && (
          <span data-testid="nav-ws-online-dot" className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-[#22C55E] border border-[#020B1F] shadow-[0_0_6px_#22C55E]" />
        )}
        {unread.length > 0 && (
          <span
            data-testid="nav-notif-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B6B] text-white text-[10px] font-display font-black grid place-items-center border-2 border-[#020B1F] shadow-[0_0_8px_rgba(255,59,107,0.6)]"
          >
            {unread.length > 9 ? "9+" : unread.length}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="nav-notif-panel"
          className="absolute right-0 top-full mt-2 w-[300px] sm:w-[360px] glass-strong rounded-2xl border border-[#00AFFF]/30 shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00AFFF] font-bold"><Tx>Benachrichtigungen</Tx></div>
            {unread.length > 0 && (
              <button
                onClick={markAllRead}
                data-testid="nav-notif-mark-read"
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-white/65 hover:text-[#22C55E]"
              >
                <Check size={11}/> <Tx>Alle gelesen</Tx>
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-white/45"><Tx>Keine Benachrichtigungen.</Tx></div>
            ) : items.map((n) => (
              <Link
                key={n.id}
                to={n.link || "/"}
                data-testid={`notif-${n.id}`}
                onClick={() => setOpen(false)}
                className={`flex items-start gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition ${n.read ? "" : "bg-[#00AFFF]/5"}`}
              >
                <span className={`mt-1 w-2 h-2 rounded-full shrink-0 ${n.read ? "bg-white/20" : "bg-[#00AFFF] shadow-[0_0_8px_#00AFFF]"}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-display font-bold text-white truncate">{n.title || n.type}</div>
                  {n.body && <div className="text-[11px] text-white/65 mt-0.5 line-clamp-2">{n.body}</div>}
                  <div className="text-[10px] text-white/35 font-mono mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                </div>
              </Link>
            ))}
          </div>
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            data-testid="nav-notif-see-all"
            className="block text-center py-2.5 text-[11px] uppercase tracking-widest text-[#00AFFF] hover:bg-white/5 font-bold border-t border-white/10"
          >
            <Tx>Alle anzeigen</Tx> →
          </Link>
        </div>
      )}
    </div>
  );
}
